import React, { useState } from 'react';

import { BankAccountDetail } from '@flipdish/api-client-typescript';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import { RadioButton, RadioGroup } from '@flipdish/ui-library/components/atoms/RadioButton';
import { Dialog, Modal, ModalOverlay } from '@flipdish/ui-library/components/molecules/Modal';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';

import { updateBankAccountState } from '../../../banking.actions';

type StaffAccountActionsProps = {
  accountId: number;
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
type Props = StaffAccountActionsProps & MappedDispatch;

const rejectionReasons: { value: string; labelId: TranslationId }[] = [
  { value: 'Incorrect VAT Number', labelId: 'Incorrect_VAT_Number' },
  { value: 'Incorrect IBAN', labelId: 'Incorrect_IBAN' },
  { value: 'Incorrect SWIFT', labelId: 'Incorrect_SWIFT' },
];

const StaffAccountActionsInner = (props: Props) => {
  const { approve, disapprove } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('Incorrect VAT Number');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await approve();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisapprove = () => {
    setIsDialogOpen(true);
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      await disapprove(rejectionReason);
      setIsDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <Button
          color="secondary-destructive"
          size="md"
          data-fd="disapprove-account"
          isDisabled={isSubmitting}
          onPress={handleDisapprove}
          className="w-full sm:w-auto"
        >
          <Translate id="disapprove" />
        </Button>
        <Button
          color="primary"
          size="md"
          data-fd="approve-account"
          isDisabled={isSubmitting}
          onPress={handleApprove}
          className="w-full sm:w-auto"
        >
          <Translate id="approve" />
        </Button>
      </div>

      <ModalOverlay
        data-tailwind
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      >
        <Modal className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <Dialog>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              <Translate id="Reject_bank_account" />
            </h2>

            <p className="text-sm text-gray-600 mb-4">
              <Translate id="Reason_for_rejection" />
            </p>

            <RadioGroup
              value={rejectionReason}
              onChange={setRejectionReason}
              className="flex flex-col gap-2 mb-6"
            >
              {rejectionReasons.map((reason) => (
                <RadioButton
                  key={reason.value}
                  value={reason.value}
                  label={<Translate id={reason.labelId} />}
                />
              ))}
            </RadioGroup>

            <div className="flex justify-end gap-3">
              <Button
                color="secondary"
                size="md"
                data-fd="cancel-reject-account"
                isDisabled={isSubmitting}
                onPress={() => setIsDialogOpen(false)}
              >
                <Translate id="Cancel" />
              </Button>
              <Button
                color="secondary-destructive"
                size="md"
                data-fd="reject-account"
                isDisabled={isSubmitting}
                onPress={handleReject}
              >
                <Translate id="Reject" />
              </Button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};

function mapDispatchToProps(dispatch: ThunkDispatch, props: StaffAccountActionsProps) {
  const { accountId } = props;
  return {
    disapprove: (reason: string) =>
      dispatch(
        updateBankAccountState({
          accountId,
          accountState: BankAccountDetail.AccountStateEnum.Unverified,
          reason,
        })
      ),
    approve: () =>
      dispatch(
        updateBankAccountState({
          accountId,
          accountState: BankAccountDetail.AccountStateEnum.Verified,
          reason: '',
        })
      ),
  };
}

const Connected = connect(null, mapDispatchToProps)(StaffAccountActionsInner);

const StaffAccountActions = (props: StaffAccountActionsProps) => (
  <Permissions allowed={['FlipdishStaff']}>
    <Connected {...props} />
  </Permissions>
);

export default StaffAccountActions;
